import { useEffect, useRef, useCallback } from "react";

interface UseAutoSliderProps {
  interval?: number;
  enabled?: boolean;
  onTick?: () => void;
}

export function useAutoSlider({
  interval = 5000,
  enabled = true,
  onTick,
}: UseAutoSliderProps = {}) {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startAutoplay = useCallback(() => {
    if (!enabled) return;
    if (intervalRef.current) clearInterval(intervalRef.current);

    intervalRef.current = setInterval(() => {
      if (onTick) onTick();
    }, interval);
  }, [interval, enabled, onTick]);

  const stopAutoplay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resetAutoplay = useCallback(() => {
    stopAutoplay();
    startAutoplay();
  }, [stopAutoplay, startAutoplay]);

  useEffect(() => {
    if (enabled) startAutoplay();
    return () => stopAutoplay();
  }, [enabled, startAutoplay, stopAutoplay]);

  return {
    startAutoplay,
    stopAutoplay,
    resetAutoplay,
    isPlaying: !!intervalRef.current,
  };
}

export function useDomSlider() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const dragStartRef = useRef<{ x: number; scrollLeft: number } | null>(null);
  const isDraggingRef = useRef(false);

  const handleMouseDown = useCallback((e: any) => {
    if (!containerRef.current) return;
    if (e.button !== 0) return; // Only drag with left click

    dragStartRef.current = {
      x: e.clientX,
      scrollLeft: containerRef.current.scrollLeft,
    };
    isDraggingRef.current = false;
    document.body.style.userSelect = "none";
  }, []);

  const handleMouseMove = useCallback((e: any) => {
    if (!dragStartRef.current || !containerRef.current) return;
    const dx = e.clientX - dragStartRef.current.x;

    if (Math.abs(dx) > 10) {
      isDraggingRef.current = true;
    }

    if (isDraggingRef.current) {
      containerRef.current.scrollLeft = dragStartRef.current.scrollLeft - dx;
    }
  }, []);

  const handleMouseUpOrLeave = useCallback(() => {
    dragStartRef.current = null;
    document.body.style.userSelect = "";
    // Delay resetting isDragging so onClick handlers can check it
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 50);
  }, []);

  const scrollPrev = useCallback((amount = 320) => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: -amount, behavior: "smooth" });
    }
  }, []);

  const scrollNext = useCallback((amount = 320) => {
    if (containerRef.current) {
      containerRef.current.scrollBy({ left: amount, behavior: "smooth" });
    }
  }, []);

  return {
    containerRef,
    isDraggingRef,
    scrollPrev,
    scrollNext,
    sliderEvents: {
      onMouseDown: handleMouseDown,
      onMouseMove: handleMouseMove,
      onMouseUp: handleMouseUpOrLeave,
      onMouseLeave: handleMouseUpOrLeave,
    },
  };
}
